import { Copy, Download, Share2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/kit";
import { useToast } from "@/components/toast";

const canShareFiles = () => {
  try {
    return !!navigator.canShare && navigator.canShare({ files: [new File([""], "x.mp4", { type: "video/mp4" })] });
  } catch {
    return false;
  }
};

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.className = "fixed -left-[9999px] opacity-0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  }
}

/** Download = real MP4 file (Content-Disposition attachment). Share = Web Share with the file, else copy link + "Link copied". */
export function ResultActions({ job }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const name = `listingreel_${job.id}.mp4`;
  const filesOk = canShareFiles();

  // Pre-fetch the MP4 so the share sheet opens inside the click's user-activation window.
  useEffect(() => {
    if (!filesOk) return;
    let off = false;
    fetch(job.video_url).then((r) => r.blob()).then((b) => {
      if (!off) file.current = new File([b], name, { type: "video/mp4" });
    }).catch(() => {});
    return () => {
      off = true;
    };
  }, [job.video_url, name, filesOk]);

  const fallback = async () => {
    if (await copyText(job.video_url)) toast("Link copied", "success");
    else toast("Couldn't copy the link", "error");
  };

  const share = async () => {
    setBusy(true);
    try {
      if (filesOk) {
        if (!file.current) file.current = new File([await (await fetch(job.video_url)).blob()], name, { type: "video/mp4" });
        await navigator.share({ files: [file.current], title: "Listing video" });
      } else {
        await fallback();
      }
    } catch (e) {
      if (e?.name !== "AbortError") await fallback();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
      <a href={`${job.video_url}&download=1`} download={name} data-testid="result-download-button"
        className="focus-ring inline-flex min-h-14 flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-6 text-base font-semibold text-primary-foreground transition-all hover:brightness-110 active:scale-[0.98]">
        <Download className="h-5 w-5" aria-hidden /> Download MP4
      </a>
      <Button title="Share" icon={filesOk ? Share2 : Copy} variant="secondary" onClick={share} loading={busy} className="flex-1"
        data-testid="result-share-button" />
    </div>
  );
}
